<script setup>
import { computed, ref, watch } from "vue";
import { fetchUserActivityLog } from "@/services/backofficeClient";

const props = defineProps({
  userId: { type: String, default: "" },
  pageSize: { type: Number, default: 25 },
  compact: { type: Boolean, default: false },
  title: { type: String, default: "Activity log" },
});

const entries = ref([]);
const loading = ref(false);
const loadingMore = ref(false);
const error = ref("");
const page = ref(1);
const totalItems = ref(0);
const hasNext = ref(false);

const fetchPage = async (targetPage) => {
  const payload = await fetchUserActivityLog(props.userId, {
    page: targetPage,
    pageSize: props.pageSize,
  });
  const items = Array.isArray(payload?.items) ? payload.items : [];
  totalItems.value = payload?.pagination?.totalItems ?? items.length;
  hasNext.value = Boolean(payload?.pagination?.hasNext);
  page.value = targetPage;
  return items;
};

const load = async () => {
  entries.value = [];
  error.value = "";
  page.value = 1;
  totalItems.value = 0;
  hasNext.value = false;
  if (!props.userId) return;
  loading.value = true;
  try {
    entries.value = await fetchPage(1);
  } catch (err) {
    error.value = err?.payload?.message || err?.message || "Failed to load activity log.";
  } finally {
    loading.value = false;
  }
};

const loadMore = async () => {
  if (loadingMore.value || !hasNext.value) return;
  loadingMore.value = true;
  try {
    const items = await fetchPage(page.value + 1);
    entries.value = [...entries.value, ...items];
  } catch (err) {
    error.value = err?.payload?.message || err?.message || "Failed to load activity log.";
  } finally {
    loadingMore.value = false;
  }
};

watch(() => props.userId, load, { immediate: true });

defineExpose({ reload: load });

const formatDate = (value) => {
  if (!value) return "--";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "--";
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  const hours = `${date.getHours()}`.padStart(2, "0");
  const minutes = `${date.getMinutes()}`.padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}`;
};

const actorLabel = (entry) => {
  const actor = entry?.actor;
  if (!actor) return "Unknown";
  if (actor.type === "system" && actor.source) return `System (${actor.source})`;
  return actor.name || actor.type || "Unknown";
};

const actorBadgeClass = (entry) => {
  switch (entry?.actor?.type) {
    case "admin":
      return "badge badge-sm bg-gradient-info";
    case "system":
      return "badge badge-sm bg-gradient-secondary";
    default:
      return "badge badge-sm bg-gradient-light text-dark";
  }
};

const verificationBorderClass = (status) => {
  switch (status) {
    case "verified":
      return "border-success";
    case "rejected_code":
    case "rejected_selfie":
      return "border-danger";
    case "required":
      return "border-warning";
    default:
      return "border-info";
  }
};

const statusBorderClass = (entry) => {
  if (entry?.type === "warningSent") return "border-warning";
  if (entry?.type === "verificationStatusChanged") {
    return verificationBorderClass(entry.toVerificationStatus);
  }
  switch (entry?.toStatus) {
    case "active":
      return "border-success";
    case "blocked":
    case "deleted":
      return "border-danger";
    case "ghosted":
      return "border-warning";
    default:
      return "border-secondary";
  }
};

const formatVerificationStatus = (status) =>
  status ? status.replaceAll("_", " ") : "none";

const headline = (entry) => {
  if (entry?.type === "warningSent") return "Warning sent";
  if (entry?.type === "verificationStatusChanged") {
    const to = formatVerificationStatus(entry.toVerificationStatus);
    const from = entry.fromVerificationStatus;
    return from
      ? `Verification ${formatVerificationStatus(from)} → ${to}`
      : `Verification → ${to}`;
  }
  const to = entry?.toStatus || "?";
  const from = entry?.fromStatus;
  return from ? `Status ${from} → ${to}` : `Status → ${to}`;
};

const REFERENCE_LABELS = {
  complaint: "complaint",
  complianceRule: "rule",
  mediaComplianceRule: "media rule",
  verificationAttempt: "verification",
  media: "media",
  supportChat: "support chat",
};

const referenceLabel = (reference) => {
  const kind = REFERENCE_LABELS[reference?.type] || reference?.type || "ref";
  const id = `${reference?.id || ""}`;
  const shortId = reference?.type === "media" ? id.split("/").pop() : id.slice(-6);
  return `${kind} ${shortId}`;
};

const referenceRoute = (reference, userId) => {
  switch (reference?.type) {
    case "complaint":
      return userId ? `/complaints/reported/${userId}` : "";
    case "complianceRule":
      return "/compliance-rules";
    case "mediaComplianceRule":
      return "/media-compliance-rules";
    case "verificationAttempt":
      return "/verification";
    default:
      return "";
  }
};

const hasEntries = computed(() => entries.value.length > 0);
</script>

<template>
  <div class="user-activity-log">
    <div class="d-flex align-items-center justify-content-between gap-2 mb-2">
      <p class="text-xs text-uppercase text-secondary fw-bolder mb-0">
        {{ title }}
        <span v-if="totalItems" class="text-secondary fw-normal">({{ totalItems }})</span>
      </p>
      <button type="button" class="btn btn-link btn-sm p-0 mb-0 text-xs" :disabled="loading || !userId" @click="load">
        Refresh
      </button>
    </div>
    <div v-if="error" class="text-danger text-xs">{{ error }}</div>
    <div v-else-if="loading" class="text-xs text-secondary">Loading…</div>
    <div v-else-if="!hasEntries" class="text-xs text-secondary">
      Nothing recorded yet.
    </div>
    <template v-else>
      <div :class="compact ? 'user-activity-log-scroll' : ''">
        <div v-for="entry in entries" :key="entry.id" class="border-start border-2 ps-2 mb-2"
          :class="statusBorderClass(entry)">
          <div class="d-flex align-items-center gap-1 flex-wrap">
            <span class="text-xs fw-semibold">{{ headline(entry) }}</span>
            <span :class="actorBadgeClass(entry)">{{ actorLabel(entry) }}</span>
          </div>
          <div class="text-xxs text-secondary">{{ formatDate(entry.createdAt) }}</div>
          <div class="text-xs text-dark fst-italic mt-1">{{ entry.reason }}</div>
          <div v-if="entry.references?.length" class="d-flex flex-wrap gap-1 mt-1">
            <template v-for="(reference, index) in entry.references" :key="`${entry.id}-${index}`">
              <router-link v-if="referenceRoute(reference, entry.userId)" :to="referenceRoute(reference, entry.userId)"
                :title="reference.id" class="badge bg-light text-dark border text-xxs fw-light text-decoration-none">
                {{ referenceLabel(reference) }}
              </router-link>
              <span v-else :title="reference.id" class="badge bg-light text-dark border text-xxs fw-light">
                {{ referenceLabel(reference) }}
              </span>
            </template>
          </div>
        </div>
      </div>
      <button v-if="hasNext" type="button" class="btn btn-sm btn-outline-secondary w-100 mb-0 mt-1"
        :disabled="loadingMore" @click="loadMore">
        <span v-if="loadingMore" class="spinner-border spinner-border-sm me-1"></span>
        Load more ({{ entries.length }} of {{ totalItems }})
      </button>
    </template>
  </div>
</template>

<style scoped>
.user-activity-log-scroll {
  max-height: 260px;
  overflow-y: auto;
}
</style>
